import { BuffOrDebuff, WarbandMemberDTO } from "../types";
import { getBuffName } from "./formatting";

export interface StatModifier {
    label: string;
    value: number;
    type: 'add' | 'set' | 'rule';
}

/** Limits a buff's delta so caps never erase other bonuses and floors never invert. */
const applyBuffDelta = (current: number, raw: number, minimum?: number | null, maximum?: number | null): number => {
    let next = current + raw;
    if (maximum != null && raw > 0) next = Math.min(next, Math.max(current, maximum));
    if (minimum != null && raw < 0) next = Math.max(next, Math.min(current, minimum));
    return next - current;
};

/** Shrinking Potion removes the carrying-treasure Move/Fight penalties. */
const hasShrinkingPotion = (buffs: BuffOrDebuff[]): boolean =>
    buffs.some((buff) => buff.sourceId === 'shrinking_potion');

export const getStatModifiers = (member: WarbandMemberDTO, stat: string, base: number, warbandBuffs?: BuffOrDebuff[]): StatModifier[] => {
    const modifiers: StatModifier[] = [];
    let adjusted = base;

    const weapons = member.weapons ?? [];
    const armours = member.armours ?? [];

    const memberBuffs = member.buffsAndDebuffs ?? [];
    const activeWarbandBuffs = (warbandBuffs ?? []).filter(b => b.globalYou > 0);
    const allBuffs = [...memberBuffs, ...activeWarbandBuffs];

    const injuries = member.permanentInjuries ?? [];

    switch (stat) {
        case 'move':
            allBuffs.forEach(buff => {
                if (buff.moveChangeType === 'add' && buff.moveChangeValue != null) {
                    const delta = applyBuffDelta(adjusted, buff.moveChangeValue * (buff.stackCount ?? 1), buff.moveMinimum, buff.moveMaximum);
                    modifiers.push({ label: getBuffName(buff), value: delta, type: 'add' });
                    adjusted += delta;
                }
            });
            allBuffs.forEach(buff => {
                if (buff.moveChangeType === 'set' && buff.moveChangeValue != null) {
                    modifiers.push({ label: getBuffName(buff), value: buff.moveChangeValue, type: 'set' });
                    adjusted = buff.moveChangeValue;
                }
            });
            injuries.forEach(injury => {
                if (injury.moveChange != null) {
                    modifiers.push({ label: injury.name, value: injury.moveChange, type: 'add' });
                    adjusted += injury.moveChange;
                }
            });
            if (member.carryingTreasure && !hasShrinkingPotion(allBuffs)) {
                modifiers.push({ label: 'Carrying Treasure', value: -(adjusted / 2), type: 'rule' });
            }
            break;

        case 'fight': {
            allBuffs.forEach(buff => {
                if (buff.fightChangeType === 'add' && buff.fightChangeValue != null) {
                    const delta = applyBuffDelta(adjusted, buff.fightChangeValue * (buff.stackCount ?? 1), buff.fightMinimum, buff.fightMaximum);
                    modifiers.push({ label: getBuffName(buff), value: delta, type: 'add' });
                    adjusted += delta;
                }
            });
            allBuffs.forEach(buff => {
                if (buff.fightChangeType === 'set' && buff.fightChangeValue != null) {
                    modifiers.push({ label: getBuffName(buff), value: buff.fightChangeValue, type: 'set' });
                    adjusted = buff.fightChangeValue;
                }
            });
            injuries.forEach(injury => {
                if (injury.fightChange != null) {
                    modifiers.push({ label: injury.name, value: injury.fightChange, type: 'add' });
                    adjusted += injury.fightChange;
                }
            });
            const hasWeaponWithFightModifier = weapons.some(w => w.encumbering !== null && w.encumbering > 0);
            const hasArmourWithFightModifier = armours.some(a => a.encumbering !== null && a.encumbering > 0);
            if (member.carryingTreasure && !hasShrinkingPotion(allBuffs) && (hasWeaponWithFightModifier || hasArmourWithFightModifier)) {
                modifiers.push({ label: 'Carrying Treasure', value: -1, type: 'rule' });
            }
            break;
        }

        case 'shoot':
            allBuffs.forEach(buff => {
                if (buff.shootChangeType === 'add' && buff.shootChangeValue != null) {
                    const delta = applyBuffDelta(adjusted, buff.shootChangeValue * (buff.stackCount ?? 1), buff.shootMinimum, buff.shootMaximum);
                    modifiers.push({ label: getBuffName(buff), value: delta, type: 'add' });
                    adjusted += delta;
                }
            });
            allBuffs.forEach(buff => {
                if (buff.shootChangeType === 'set' && buff.shootChangeValue != null) {
                    modifiers.push({ label: getBuffName(buff), value: buff.shootChangeValue, type: 'set' });
                    adjusted = buff.shootChangeValue;
                }
            });
            injuries.forEach(injury => {
                if (injury.shootChange != null) {
                    modifiers.push({ label: injury.name, value: injury.shootChange, type: 'add' });
                    adjusted += injury.shootChange;
                }
            });
            break;

        case 'armour': {
            allBuffs.forEach(buff => {
                if (buff.armourChangeType === 'add' && buff.armourChangeValue != null) {
                    const delta = applyBuffDelta(adjusted, buff.armourChangeValue * (buff.stackCount ?? 1), buff.armourMinimum, buff.armourMaximum);
                    modifiers.push({ label: getBuffName(buff), value: delta, type: 'add' });
                    adjusted += delta;
                }
            });
            allBuffs.forEach(buff => {
                if (buff.armourChangeType === 'set' && buff.armourChangeValue != null) {
                    modifiers.push({ label: getBuffName(buff), value: buff.armourChangeValue, type: 'set' });
                    adjusted = buff.armourChangeValue;
                }
            });

            break;
        }

        case 'will':
            allBuffs.forEach(buff => {
                if (buff.willChangeType === 'add' && buff.willChangeValue != null) {
                    const delta = applyBuffDelta(adjusted, buff.willChangeValue * (buff.stackCount ?? 1), buff.willMinimum, buff.willMaximum);
                    modifiers.push({ label: getBuffName(buff), value: delta, type: 'add' });
                    adjusted += delta;
                }
            });
            allBuffs.forEach(buff => {
                if (buff.willChangeType === 'set' && buff.willChangeValue != null) {
                    modifiers.push({ label: getBuffName(buff), value: buff.willChangeValue, type: 'set' });
                    adjusted = buff.willChangeValue;
                }
            });
            injuries.forEach(injury => {
                if (injury.willChange != null) {
                    modifiers.push({ label: injury.name, value: injury.willChange, type: 'add' });
                    adjusted += injury.willChange;
                }
            });
            break;

        case 'health':
            allBuffs.forEach(buff => {
                if (buff.healthChangeType === 'add' && buff.healthChangeValue != null) {
                    const delta = applyBuffDelta(adjusted, buff.healthChangeValue * (buff.stackCount ?? 1), null, null);
                    modifiers.push({ label: getBuffName(buff), value: delta, type: 'add' });
                    adjusted += delta;
                }
            });
            allBuffs.forEach(buff => {
                if (buff.healthChangeType === 'set' && buff.healthChangeValue != null) {
                    modifiers.push({ label: getBuffName(buff), value: buff.healthChangeValue, type: 'set' });
                    adjusted = buff.healthChangeValue;
                }
            });
            injuries.forEach(injury => {
                if (injury.healthChange != null) {
                    modifiers.push({ label: injury.name, value: injury.healthChange, type: 'add' });
                    adjusted += injury.healthChange;
                }
            });
            break;

        default:
            break;
    }

    return modifiers;
};

export const getSplitStatValue = (member: WarbandMemberDTO, stat: string, base: number, warbandBuffs?: BuffOrDebuff[]) => {
    let adjusted = base;

    const weapons = member.weapons ?? [];
    const armours = member.armours ?? [];

    // Combine member buffs with warband-wide buffs that apply to friendly members
    const memberBuffs = member.buffsAndDebuffs ?? [];
    const activeWarbandBuffs = (warbandBuffs ?? []).filter(b => b.globalYou > 0);
    const allBuffs = [...memberBuffs, ...activeWarbandBuffs];

    const injuries = member.permanentInjuries ?? [];

    switch (stat) {
        case 'move':
            allBuffs.forEach(buff => {
                if (buff.moveChangeType === 'add') {
                    adjusted += applyBuffDelta(adjusted, (buff?.moveChangeValue ?? 0) * (buff.stackCount ?? 1), buff.moveMinimum, buff.moveMaximum);
                }
            });

            allBuffs.forEach(buff => {
                if (buff.moveChangeType === 'set') {
                    adjusted = buff?.moveChangeValue ?? adjusted;
                }
            });

            injuries.forEach(injury => {
                if (injury.moveChange != null) adjusted += injury.moveChange;
            });

            adjusted = member.carryingTreasure && !hasShrinkingPotion(allBuffs) ? adjusted / 2 : adjusted;
            break;
        case 'fight': {
            allBuffs.forEach(buff => {
                if (buff.fightChangeType === 'add') {
                    adjusted += applyBuffDelta(adjusted, (buff?.fightChangeValue ?? 0) * (buff.stackCount ?? 1), buff.fightMinimum, buff.fightMaximum);
                }
            });

            allBuffs.forEach(buff => {
                if (buff.fightChangeType === 'set') {
                    adjusted = buff?.fightChangeValue ?? adjusted;
                }
            });

            injuries.forEach(injury => {
                if (injury.fightChange != null) adjusted += injury.fightChange;
            });

            const hasWeaponWithFightModifier = weapons.some(w => w.encumbering !== null && w.encumbering > 0);
            const hasArmourWithFightModifier = armours.some(a => a.encumbering !== null && a.encumbering > 0);
            adjusted = member.carryingTreasure && !hasShrinkingPotion(allBuffs) && (hasWeaponWithFightModifier || hasArmourWithFightModifier) ? adjusted - 1 : adjusted;
            break;
        }
        case 'shoot':
            allBuffs.forEach(buff => {
                if (buff.shootChangeType === 'add') {
                    adjusted += applyBuffDelta(adjusted, (buff?.shootChangeValue ?? 0) * (buff.stackCount ?? 1), buff.shootMinimum, buff.shootMaximum);
                }
            });

            allBuffs.forEach(buff => {
                if (buff.shootChangeType === 'set') {
                    adjusted = buff?.shootChangeValue ?? adjusted;
                }
            });

            injuries.forEach(injury => {
                if (injury.shootChange != null) adjusted += injury.shootChange;
            });
            break;
        case 'armour': {
            allBuffs.forEach(buff => {
                if (buff.armourChangeType === 'add') {
                    adjusted += applyBuffDelta(adjusted, (buff?.armourChangeValue ?? 0) * (buff.stackCount ?? 1), buff.armourMinimum, buff.armourMaximum);
                }
            });

            allBuffs.forEach(buff => {
                if (buff.armourChangeType === 'set') {
                    adjusted = buff?.armourChangeValue ?? adjusted;
                }
            });

            break;
        }
        case 'will':
            allBuffs.forEach(buff => {
                if (buff.willChangeType === 'add') {
                    adjusted += applyBuffDelta(adjusted, (buff?.willChangeValue ?? 0) * (buff.stackCount ?? 1), buff.willMinimum, buff.willMaximum);
                }
            });

            allBuffs.forEach(buff => {
                if (buff.willChangeType === 'set') {
                    adjusted = buff?.willChangeValue ?? adjusted;
                }
            });

            injuries.forEach(injury => {
                if (injury.willChange != null) adjusted += injury.willChange;
            });
            break;
        case 'health':
            allBuffs.forEach(buff => {
                if (buff.healthChangeType === 'add') {
                    adjusted += (buff?.healthChangeValue ?? 0) * (buff.stackCount ?? 1);
                }
            });

            allBuffs.forEach(buff => {
                if (buff.healthChangeType === 'set') {
                    adjusted = buff?.healthChangeValue ?? adjusted;
                }
            });

            injuries.forEach(injury => {
                if (injury.healthChange != null) adjusted += injury.healthChange;
            });
            break;
        default:
            break;
    }

    if (adjusted === undefined || adjusted === base) {
        return base;
    }

    return adjusted;
}
